import { AssignmentTaskSlideBlockParam } from '@consts';
import { AssignmentTaskSlideType } from '@models/assignments';

export interface SlideCalculationResult {
  slideType: AssignmentTaskSlideType;
  blockEntityId: string | null;
}

export class AssignmentTaskNavigation {
  static getBlockIdForSlide(
    slideType: AssignmentTaskSlideType,
    blockEntityId: string | null
  ): string | undefined {
    switch (slideType) {
      case AssignmentTaskSlideType.BLOCK:
        return blockEntityId;
      case AssignmentTaskSlideType.START_SLIDE:
        return AssignmentTaskSlideBlockParam.START_BLOCK_SYNTHETIC_ID;
      case AssignmentTaskSlideType.END_SLIDE:
        return AssignmentTaskSlideBlockParam.END_BLOCK_SYNTHETIC_ID;
      default:
        return AssignmentTaskSlideBlockParam.START_BLOCK_SYNTHETIC_ID;
    }
  }

  static calculateNextSlide(
    currentSlideType: AssignmentTaskSlideType,
    currentBlockEntityId: string | null,
    blocks: Array<{ entityId: string }>
  ): SlideCalculationResult {
    const numberOfBlocks = blocks.length;

    if (currentSlideType === AssignmentTaskSlideType.START_SLIDE) {
      if (numberOfBlocks === 0) {
        return { slideType: AssignmentTaskSlideType.END_SLIDE, blockEntityId: null };
      }
      return {
        slideType: AssignmentTaskSlideType.BLOCK,
        blockEntityId: blocks[0].entityId
      };
    }

    if (currentBlockEntityId === blocks[numberOfBlocks - 1]?.entityId) {
      return { slideType: AssignmentTaskSlideType.END_SLIDE, blockEntityId: null };
    }

    const currentBlockIdx = blocks.findIndex((block) => block.entityId === currentBlockEntityId);

    // Safety check: if block not found (-1) or at last position (numberOfBlocks - 1), go to END_SLIDE
    if (currentBlockIdx === -1 || currentBlockIdx === numberOfBlocks - 1) {
      return {
        slideType: AssignmentTaskSlideType.END_SLIDE,
        blockEntityId: null
      };
    }
    return {
      slideType: AssignmentTaskSlideType.BLOCK,
      blockEntityId: blocks[currentBlockIdx + 1]?.entityId || null
    };
  }

  static calculatePrevSlide(
    currentSlideType: AssignmentTaskSlideType,
    currentBlockEntityId: string | null,
    blocks: Array<{ entityId: string }>
  ): SlideCalculationResult {
    const numberOfBlocks = blocks.length;

    if (currentSlideType === AssignmentTaskSlideType.END_SLIDE) {
      if (numberOfBlocks === 0) {
        return { slideType: AssignmentTaskSlideType.START_SLIDE, blockEntityId: null };
      }
      return {
        slideType: AssignmentTaskSlideType.BLOCK,
        blockEntityId: blocks[numberOfBlocks - 1]?.entityId
      };
    }

    if (currentBlockEntityId === blocks[0]?.entityId) {
      return { slideType: AssignmentTaskSlideType.START_SLIDE, blockEntityId: null };
    }

    const currentBlockIdx = blocks.findIndex((block) => block.entityId === currentBlockEntityId);

    // Safety check: if block not found (-1) or at first position (0), go to START_SLIDE
    if (currentBlockIdx === -1 || currentBlockIdx === 0) {
      return {
        slideType: AssignmentTaskSlideType.START_SLIDE,
        blockEntityId: null
      };
    }
    return {
      slideType: AssignmentTaskSlideType.BLOCK,
      blockEntityId: blocks[currentBlockIdx - 1]?.entityId || null
    };
  }

  static parseBlockIdToSlide(
    blockEntityId: string | string[] | undefined
  ): { type: AssignmentTaskSlideType; blockEntityId: string | null } | undefined {
    if (!blockEntityId) return undefined;
    const val = Array.isArray(blockEntityId) ? blockEntityId[0] : blockEntityId;

    if (val === AssignmentTaskSlideBlockParam.START_BLOCK_SYNTHETIC_ID) {
      return { type: AssignmentTaskSlideType.START_SLIDE, blockEntityId: null };
    }
    if (val === AssignmentTaskSlideBlockParam.END_BLOCK_SYNTHETIC_ID) {
      return { type: AssignmentTaskSlideType.END_SLIDE, blockEntityId: null };
    }
    return { type: AssignmentTaskSlideType.BLOCK, blockEntityId: val };
  }
}
